import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Button, Skeleton, useTheme } from "@mui/material";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import ShoppingBagOutlined from "@mui/icons-material/ShoppingBagOutlined";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import LocalOfferOutlined from "@mui/icons-material/LocalOfferOutlined";
import PeopleOutlineRounded from "@mui/icons-material/PeopleOutlineRounded";
import CurrencyRupeeRounded from "@mui/icons-material/CurrencyRupeeRounded";
import ViewCarouselOutlined from "@mui/icons-material/ViewCarouselOutlined";
import AuthAPI from "../../../api/firebase/AuthAPI";
import OrdersAPI from "../../../api/firebase/OrdersAPI";
import ProductsAPI from "../../../api/firebase/ProductsAPI";
import { money, summarizeDashboard, toDate } from "./dashboardData";
import { RevenueTrendChart, OrderStatusDonut, CategoryBarChart, TopCustomersChart, TopProductsChart } from "./DashboardCharts";
import "./styles.css";

const initialResource = { data: [], loading: true, error: false };
const countFormat = (value) => value.toLocaleString("en-IN");
const dateFormat = (value) => toDate(value)?.toLocaleDateString("en-IN", {
  day: "numeric", month: "short", year: "numeric",
}) || "Date unavailable";

function Metric({ label, value, note, icon, to, resource, accent, tone, delay = 0 }) {
  return (
    <Link
      to={to}
      className={`admin-dashboard__metric admin-dashboard__metric--${tone} ${accent ? "admin-dashboard__metric--accent" : ""}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="admin-dashboard__metric-top">
        <span className="admin-dashboard__metric-label">{label}</span>
        <span className="admin-dashboard__icon" aria-hidden="true">{icon}</span>
      </div>
      <div className="admin-dashboard__metric-value">{resource.loading ? <Skeleton width="65%" /> : resource.error ? "—" : value}</div>
      <div className="admin-dashboard__metric-bottom">
        <span>{resource.error ? "Data unavailable" : note}</span>
        <span className="admin-dashboard__metric-arrow" aria-hidden="true"><ArrowForwardRounded /></span>
      </div>
    </Link>
  );
}

export default function AdminDashboard() {
  const theme = useTheme();
  const [orders, setOrders] = useState(initialResource);
  const [products, setProducts] = useState(initialResource);
  const [users, setUsers] = useState(initialResource);
  const [refresh, setRefresh] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);
  const loading = orders.loading || products.loading || users.loading;
  const failed = [orders.error && "orders", products.error && "products", users.error && "users"].filter(Boolean);

  useEffect(() => {
    let cancelled = false;
    setUpdatedAt(null);
    const fetchResource = async (request, setter, filter) => {
      setter({ data: [], loading: true, error: false });
      try {
        const result = await request();
        if (!result?.success || !Array.isArray(result.data)) throw new Error("Unavailable");
        if (!cancelled) setter({ data: result.data.filter(filter), loading: false, error: false });
      } catch {
        if (!cancelled) setter({ data: [], loading: false, error: true });
      }
    };
    Promise.all([
      fetchResource(
        () => new OrdersAPI().getOrders(),
        setOrders,
        (order) => String(order.status || "").trim().toUpperCase() !== "CREATED"
      ),
      fetchResource(() => new ProductsAPI().getProducts(), setProducts, () => true),
      fetchResource(() => new AuthAPI().getAllUsers(), setUsers, (user) => !user.role?.includes("Admin")),
    ]).then(() => { if (!cancelled) setUpdatedAt(new Date()); });
    return () => { cancelled = true; };
  }, [refresh]);

  const stats = summarizeDashboard(orders.data, products.data, users.data);

  return (
    <div className="admin-dashboard" data-theme={theme.palette.mode}>
      <header className="admin-dashboard__header admin-dashboard__hero">
        <div><p className="admin-dashboard__eyebrow">YOUR STORE AT A GLANCE</p><h1>Dashboard</h1><p className="admin-dashboard__subtitle">A little clarity for your day. Here’s how your store is doing.</p></div>
        <div className="admin-dashboard__header-actions">
          <span className="admin-dashboard__date">{dateFormat(new Date())}</span>
          <Button variant="outlined" startIcon={<RefreshRounded />} disabled={loading} onClick={() => setRefresh((value) => value + 1)}>{loading ? "Refreshing…" : "Refresh"}</Button>
        </div>
      </header>
      <div className="admin-dashboard__section-label"><h2>Store overview</h2><span>All time <span aria-hidden="true">·</span> <span role="status">{loading ? "Loading data…" : failed.length ? "Some data unavailable" : updatedAt ? `Updated ${updatedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : ""}</span></span></div>
      {failed.length > 0 && <Alert severity="warning" className="admin-dashboard__alert">Couldn’t load {failed.join(", ")}. Refresh to try again.</Alert>}
      {!orders.loading && !orders.error && stats.placed > 0 && (
        <Alert
          severity="warning"
          className="admin-dashboard__alert admin-dashboard__placed-alert"
          action={
            <Button
              component={Link}
              to="/admin/orders"
              color="inherit"
              size="small"
              endIcon={<ArrowForwardRounded />}
            >
              Review orders
            </Button>
          }
        >
          <strong>
            {countFormat(stats.placed)} placed {stats.placed === 1 ? "order is" : "orders are"} awaiting shipment.
          </strong>{" "}
          <span className="admin-dashboard__alert-hint">
            Review and process {stats.placed === 1 ? "it" : "them"} now.
          </span>
        </Alert>
      )}
      <section className="admin-dashboard__metrics" aria-label="Store totals" aria-busy={loading}>
        <Metric label="Total order value" value={money(stats.total)} note="Excludes draft and cancelled orders" icon={<CurrencyRupeeRounded />} to="/admin/orders" resource={orders} tone="revenue" accent />
        <Metric label="Order Placed" value={countFormat(orders.data.length)} note={orders.loading ? "Loading orders…" : `${countFormat(stats.placed)} awaiting shipment`} icon={<ShoppingBagOutlined />} to="/admin/orders" resource={orders} tone="orders" delay={70} />
        <Metric label="Products" value={countFormat(products.data.length)} note={products.loading ? "Loading inventory…" : `${countFormat(stats.hidden)} hidden from storefront`} icon={<Inventory2Outlined />} to="/admin/products" resource={products} tone="products" delay={140} />
        <Metric label="Registered users" value={countFormat(users.data.length)} note={users.loading ? "Loading users…" : `${countFormat(stats.customers)} have placed an order`} icon={<PeopleOutlineRounded />} to="/admin/users" resource={users} tone="users" delay={210} />
      </section>

      <div className="admin-dashboard__section-label admin-dashboard__section-label--insights">
        <h2>Product & Catalog Insights</h2>
        <span>Top selling products and category volume</span>
      </div>
      <div className="admin-dashboard__main-grid admin-dashboard__product-insights-grid">
        <TopProductsChart orders={orders.data} products={products.data} loading={orders.loading || products.loading} error={orders.error} />
        <CategoryBarChart products={products.data} loading={products.loading} error={products.error} />
      </div>

      <div className="admin-dashboard__section-label admin-dashboard__section-label--insights">
        <h2>Customer Insights</h2>
        <span>Your most frequent shoppers</span>
      </div>
      <div className="admin-dashboard__customer-insights">
        <TopCustomersChart orders={orders.data} users={users.data} loading={orders.loading} error={orders.error} />
      </div>

      <div className="admin-dashboard__section-label">
        <h2>Performance & Trends</h2>
        <span>Sales trajectory and fulfillment breakdown</span>
      </div>
      <div className="admin-dashboard__main-grid">
        <RevenueTrendChart orders={orders.data} loading={orders.loading} error={orders.error} />
        <OrderStatusDonut orders={orders.data} loading={orders.loading} error={orders.error} />
      </div>

      <nav className="admin-dashboard__shortcuts" aria-label="Store management shortcuts">
        <div className="admin-dashboard__shortcuts-heading">
          <span className="admin-dashboard__shortcuts-kicker">Quick actions</span>
          <h2>Make it yours</h2>
          <p>Keep your storefront fresh and ready to shop.</p>
        </div>
        <div className="admin-dashboard__shortcut-links">
          <Link to="/admin/products">
            <span className="admin-dashboard__shortcut-icon" aria-hidden="true"><Inventory2Outlined /></span>
            <span className="admin-dashboard__shortcut-copy"><strong>Products</strong><small>Update pricing and inventory</small></span>
            <ArrowForwardRounded className="admin-dashboard__shortcut-arrow" />
          </Link>
          <Link to="/admin/coupons">
            <span className="admin-dashboard__shortcut-icon" aria-hidden="true"><LocalOfferOutlined /></span>
            <span className="admin-dashboard__shortcut-copy"><strong>Coupons</strong><small>Create offers that convert</small></span>
            <ArrowForwardRounded className="admin-dashboard__shortcut-arrow" />
          </Link>
          <Link to="/admin/sliders">
            <span className="admin-dashboard__shortcut-icon" aria-hidden="true"><ViewCarouselOutlined /></span>
            <span className="admin-dashboard__shortcut-copy"><strong>Banners</strong><small>Refresh storefront highlights</small></span>
            <ArrowForwardRounded className="admin-dashboard__shortcut-arrow" />
          </Link>
        </div>
      </nav>
    </div>
  );
}
